import React, { FC, memo, useState } from "react";
import { Collapse, IconButton } from "@mui/material";
import { useFormContext, useFormState } from "react-hook-form";
import { InfoOutlined } from "@mui/icons-material";
import {
  FormBaseType,
  FormChildQuestionDto,
  FormQuestionDto,
  FormSectionDto,
} from "@green0meter/api-client";
import { isEmpty } from "lodash";

import Note from "../Note";
import LockedBy from "./components/LockedBy";
import PreviousSubmission from "./components/PreviousSubmission";
import Input from "./components/Input";
import DraftAutosave from "./components/DraftAutosave";
import NoteButton from "./components/NoteButton";
import EditQuestion from "./components/EditQuestion";
import ConditionalModal from "./components/ConditionalModal";
import DraftSaveStatus from "./components/DraftSaveStatus";

import { useDraftField } from "@hooks/useDraftField";
import {
  CustomFormQuestionDto,
  highlightMatch,
  useMultistepFormStore,
} from "@components/common/MultistepForm";
import { Box, Stack, Typography, Alert, Tooltip, Chip } from "@components/base";

interface QuestionProps {
  questionNumber?: number;
  question: FormQuestionDto & FormChildQuestionDto;
  /** Optional sub‑questions that should be rendered below this parent */
  subQuestions?: Array<FormQuestionDto & FormChildQuestionDto>;
  section?: FormSectionDto;
  minimal?: boolean;
}

const Question: FC<QuestionProps> = ({
  questionNumber,
  question,
  subQuestions,
  section,
  minimal,
}) => {
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const { control } = useFormContext();
  const { errors } = useFormState({ control, name: question.code });
  const {
    activeInfo,
    setActiveInfo,
    searchQuery,
    formSettings: { editMode, formBasics },
  } = useMultistepFormStore();

  const isCsobForm = [
    FormBaseType.CsobClient,
    FormBaseType.CsobCommercialRealityAsset,
    FormBaseType.CsobEnergyAsset,
  ].includes(formBasics.formBaseType);

  const questionIndentationSize = 6;
  const questionIndentationAmount = Math.min(
    (questionNumber ?? 1) - 1,
    question.indentation ?? 0,
  );
  const questionIndentationLevel = isCsobForm
    ? questionIndentationAmount * questionIndentationSize
    : undefined;

  const formId = formBasics?.id;
  const draftField = useDraftField({
    formId: formId,
    fieldId: question.questionId,
  });

  const handleNoteClick = (): void => {
    setIsNoteOpen((prev) => !prev);
  };

  const handleInfoClick = (): void => {
    if (activeInfo && activeInfo.title === question.name) {
      setActiveInfo(null);
    } else {
      setActiveInfo({
        title: question.name,
        description: question.description,
      });
    }
  };

  const isSubQuestion = Boolean(question?.parentQuestionId);

  const content = minimal ? (
    <Box>
      <Input
        formBasics={formBasics}
        section={section}
        question={question}
        // draftField={draftField}
      />
    </Box>
  ) : (
    <DraftSaveStatus
      question={question}
      questionIndentationLevel={questionIndentationLevel}
    >
      <Stack direction="column">
        {editMode && (
          <Box mb={2}>
            <Chip label={`${question.code}`} size="small" color="info" />
          </Box>
        )}
        <Stack direction="row" flexGrow={1} justifyContent="space-between">
          <Typography fontSize={14} mr={3}>
            {!isCsobForm && questionNumber && `${questionNumber}. `}
            {highlightMatch(question.name, searchQuery)}
            {question.isRequired && (
              <Typography fontSize={16} component="span" color="error">
                {" *"}
              </Typography>
            )}
            {question.description && (
              <Tooltip
                title={
                  <div
                    dangerouslySetInnerHTML={{ __html: question.description }}
                  ></div>
                }
              >
                <IconButton onClick={handleInfoClick}>
                  <InfoOutlined
                    sx={({ palette }) => ({
                      color: palette.grey[400],
                      fontSize: "1rem",
                    })}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
          <Stack direction="row" alignItems={"center"} spacing={1}>
            {!editMode && (
              <NoteButton
                question={question}
                onClick={handleNoteClick}
                isOpen={isNoteOpen}
              />
            )}
            {editMode && section && (
              <EditQuestion question={question} section={section} />
            )}
          </Stack>
        </Stack>
        <Stack alignItems="end" direction="row" mt={2}>
          <Box flexGrow={1} width="100%">
            <Input
              formBasics={formBasics}
              section={section}
              question={question}
              draftField={draftField}
            />
            {draftField?.lockedBy && <LockedBy name={draftField?.lockedBy} />}
          </Box>
        </Stack>
        {errors[question.code] && (
          <Alert severity="warning" sx={{ marginTop: 1, borderRadius: 1 }}>
            {`${errors[question.code]?.message}`}
          </Alert>
        )}
        {isNoteOpen && <Note question={question} />}
        {formBasics && section && (
          <PreviousSubmission
            formId={formBasics.id}
            sectionId={section.id}
            question={question}
          />
        )}
        {formBasics && section && !editMode && (
          <DraftAutosave
            formBasics={formBasics}
            section={section}
            question={question}
          />
        )}
      </Stack>
    </DraftSaveStatus>
  );

  const renderedContent = isSubQuestion ? <Box ml={5}>{content}</Box> : content;

  return (
    <>
      <Collapse
        in={(question as CustomFormQuestionDto).isVisible}
        timeout={400}
        unmountOnExit
      >
        <Box>
          {renderedContent}
          {!isSubQuestion && subQuestions && subQuestions.length > 0 && (
            <Box ml={5} mt={2}>
              {subQuestions.map((subQ) => (
                <Question
                  key={subQ.questionId}
                  question={subQ}
                  section={section}
                  minimal={minimal}
                  questionNumber={undefined}
                />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
      {!formBasics.isLocked && !isEmpty(question.questionConditionalModals) && (
        <ConditionalModal question={question} />
      )}
    </>
  );
};

export default memo(Question);
